import type { Document as BaseDocument, SuperFormPrefillMap, ExternalError, FieldPermissions, SoftValidationWarning } from '@mes-amis/super-form';
import type { Signature } from './signature';
import type { SyncErrorType } from './analytics_event';
import type { DocumentTemplate } from './document_template';
import { isReviewEnabled } from './document_template';
import type { SelectedPerson } from './user';
import type { WarningDismissalRecord } from '@/lib/warning_dismissals';

// Need Score related types
export interface NeedScoreDefinition {
  id: number;
  label: string;
  description: string;
  scope: string; // Used as score_type in attribute naming (e.g., "strict_score", "default_score")
  slug: string; // Field identifier for attribute naming (e.g., "strict-need-score")
  min_value: number;
  max_value: number;
}

// CRDT metadata for field-level merging
// HybridLogicalClock interface lives in src/lib/crdt/hlc.ts
// Simplified: Just map field names to timestamp strings
// Timestamp format: "physical-logical-client_uuid"
export type SuperFormCRDTMetadata = Record<string, string>;

export interface CurrentNeedScore {
  id: number;
  score: number;
  unmet_need: boolean;
  comments: string;
  assessed_at: string;
}

export interface ExpandedNeedScoreField {
  definition: NeedScoreDefinition;
  current_score: CurrentNeedScore;
}

// Document type — extends the shared package type with offline/sync-specific fields.
// sync_error_type is widened from the package's union to the full SyncErrorType so the
// offline-only terminal state (completed_online) is representable.
export interface Document extends Omit<BaseDocument, 'sync_error_type'> {
  sync_error_type?: SyncErrorType | null;
  /** Digest of the payload the server refused (syncPayloadFingerprint). While
   *  it still matches the local record, the sweep holds instead of re-sending a
   *  body that can only be rejected again — see isSyncBlocked. Cleared by any
   *  outcome that replaces the sync error state. */
  sync_error_fingerprint?: string | null;
  /** When the refusal above was recorded. A record for support and analytics;
   *  the hold itself has no clock — see isSyncBlocked for what releases it. */
  sync_error_at?: string | null;
  /** Which request was refused. A document holds only the request that was
   *  actually rejected: a refused update must not stop a finalize (a different
   *  request the server may well accept), and vice versa. */
  sync_error_stage?: 'update' | 'finalize' | null;
  /** Server-computed Document#locked? — no further writes accepted (completed /
   *  signing). Present on every server payload; absent on local-only drafts. */
  locked?: boolean;
  /** Where a Finish by the syncing person lands, as of the last sync. Absent on local-only drafts. */
  finish_target?: FinishTarget;
  super_form_meta?: SuperFormCRDTMetadata;
  /** Per-field prefill metadata emitted by the server (source + requires_confirmation).
   *  Pending PARC slugs are derived client-side as
   *  `entries(field_prefills).filter(requires_confirmation) − {slugs in super_form_meta.confirmed_prefill:*}`. */
  field_prefills?: SuperFormPrefillMap;
  field_permissions?: FieldPermissions; // Per-field view permissions (slug → boolean) emitted by the server
  duplicated_from_id?: number; // Source document id when this document was created by duplicating another doc.
  pdfContent?: string; // Base64 encoded PDF content for offline use
  pdfETag?: string; // ETag from last PDF fetch for conditional requests
  pdfLastModified?: string; // Last-Modified date from last PDF fetch
  eTag?: string; // ETag from last document fetch for conditional requests
  lastModified?: string; // Last-Modified date from last document fetch
  /** Signature slots carried by the documents index/show payloads. Transient:
   *  persisted into the local `signatures` table during sync, then stripped —
   *  never stored on the document record itself. */
  signatures?: Signature[];
  locally_finalized?: boolean; // Ready for signature sync (form complete, signatures collected)
  signatures_synced?: boolean; // Signatures have been synced to server
  signature_sync_errors?: string[]; // Errors from signature sync attempts for display/retry
  completed_online_local_super_form?: Record<string, unknown>; // Unsynced local edits captured at the moment the document was found completed online
  deleted_online_had_unsynced_edits?: boolean; // Set when the document was marked deleted_online while it still had unsynced edits
  /** Fix-elsewhere validation errors from a finalize attempt that the offline
   *  form can't resolve locally (e.g. the client record's missing last name).
   *  The online finish path surfaces these in-memory; this persisted copy is for
   *  the offline path, where finalize is replayed by the background sync layer —
   *  set on a failed sync-replay finalize so the editor can show the
   *  ExternalErrorsBanner when the document is reopened, cleared on success. */
  external_errors?: ExternalError[];
  /** On-form field errors from a failed sync-replay finalize, keyed by field
   *  slug — the server can reject fields the local validation passed (e.g. a
   *  CRDT merge combined this client's values with another's). The online
   *  finish path surfaces these in-memory; this persisted copy lets the editor
   *  mark the fields and show the finish-errors banner when the document is
   *  reopened. Cleared on a successful finalize. */
  finalize_field_errors?: ServerFieldErrors;
  /** Open soft-validation warnings from a failed sync-replay finalize (its
   *  `warnings` key) — same lifecycle as `finalize_field_errors`. */
  finalize_warnings?: SoftValidationWarning[];
  /** Assessor decisions to keep an unlikely combination of answers. Synced as
   *  one list, merged per key by `changed_hlc` (see lib/warning_dismissals);
   *  items changed here since the last successful sync are `pending`. */
  warning_dismissals?: WarningDismissalRecord[];
}

/** Per-field validation errors the server returns from a rejected finalize,
 *  keyed by field slug. Shared by the finalize response parsing, the persisted
 *  {@link Document.finalize_field_errors}, and the editor's error mapping so
 *  the shape stays in lockstep across all three. */
export type ServerFieldErrors = Record<string, { message: string; label: string }>;

/** Whether the document accepts no further writes. Prefers the server's own
 *  `locked` flag (Document#locked?, carried on every server payload) so no
 *  status list has to be kept in sync with the server; the fallback covers
 *  local-only state the server never saw (e.g. a document finalized offline
 *  before its sync replay). */
export function isDocumentLocked(doc: { locked?: boolean; status?: string }): boolean {
  if (typeof doc.locked === 'boolean') return doc.locked;
  return ['completed', 'waiting_for_signature', 'signing_in_progress', 'in_review'].includes(doc.status ?? '');
}

/** Review happens only on the web: GO opens both statuses read-only, even
 *  Changes Requested, which the server leaves unlocked for web editing. */
const REVIEW_STATUSES = ['in_review', 'changes_requested'];

export function isUnderReview(doc: { status?: string }): boolean {
  return REVIEW_STATUSES.includes(doc.status ?? '');
}

export type FinishTarget = 'in_review' | 'complete';

/** A prediction from the last sync; the status the server returns on finalize is what sticks. */
export function finishTarget(
  doc: Pick<Document, 'finish_target' | 'program_id'>,
  template: DocumentTemplate | null | undefined,
  person: Pick<SelectedPerson, 'document_review_scopes'> | null | undefined
): FinishTarget {
  if (doc.finish_target) return doc.finish_target;

  const scope = doc.program_id != null ? person?.document_review_scopes?.[doc.program_id] : undefined;
  return isReviewEnabled(template) && scope === 'requires_review' ? 'in_review' : 'complete';
}
